import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as microsoftTeams from '@microsoft/teams-js';
import getRouterBasename from '@/lib/router';

type ExchangeResponse = {
  success?: boolean;
  error?: string;
};

export default function TeamsTab() {
  const [status, setStatus] = useState<'init' | 'authenticating' | 'success' | 'error'>('init');
  const [error, setError] = useState<string>('');
  const [validateInfo, setValidateInfo] = useState<any>(null);
  const [teamsToken, setTeamsToken] = useState<string | null>(null); // Store Teams token
  const navigate = useNavigate();

  const exchangeToken = useCallback(async (token: string) => {
    try {
      console.log('[TeamsTab] Exchanging token with backend');
      // Store the token for future use
      setTeamsToken(token);

      // Make the token immediately available to the fetch interceptor
      document.cookie = `teams_auth_token=${token}; path=/; SameSite=None; Secure`;

      // Use Authorization + X-Auth-Token so the first call succeeds
      console.log('[TeamsTab] Using Authorization header approach - calling auth endpoint with Bearer token');
      
      setStatus('authenticating');
      const res = await fetch(`${getRouterBasename()}/auth/header`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Auth-Token': token,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
      });

      console.log('[TeamsTab] Direct auth response status:', res.status);
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Direct auth failed: ${res.status} - ${errorText}`);
      }

      console.log('[TeamsTab] Direct authentication successful, real user JWT should be set');
      setStatus('success');
      
      // Navigate immediately - no delays needed since we have real user from start
      console.log('[TeamsTab] Navigating to app with real user authentication');
      navigate('/', { replace: true });
    } catch (e: any) {
      console.error('[TeamsTab] Token exchange error:', e);
      setError(e?.message || 'Token exchange failed');
      setStatus('error');
    }
  }, [navigate]);

  const getTokenSilently = useCallback(async () => {
    setError('');
    setStatus('authenticating');
    console.log('[TeamsTab] Starting silent token acquisition');

    try {
      // Initialize for both v2 (app.initialize) and legacy v1 (initialize)
      const mt: any = microsoftTeams as any;
      if (mt?.app?.initialize) {
        console.log('[TeamsTab] Using Teams SDK v2 app.initialize()');
        await mt.app.initialize();
      } else if (mt?.initialize) {
        console.log('[TeamsTab] Using Teams SDK v1 initialize()');
        await mt.initialize();
      } else {
        console.log('[TeamsTab] Warning: No Teams SDK initialize method found');
      }

      // Optional: read context if needed
      // const ctx = await microsoftTeams.app.getContext();

      console.log('[TeamsTab] Initialized Teams SDK, calling getAuthToken silently');
      // Try Teams SSO silent token
      await new Promise<void>((resolve, reject) => {
        (microsoftTeams as any).authentication.getAuthToken({
          silent: true,
          successCallback: (token: string) => {
            console.log('[TeamsTab] Silent token acquisition succeeded');
            exchangeToken(token);
            resolve();
          },
          failureCallback: (reason: string) => {
            console.log('[TeamsTab] Silent token acquisition failed:', reason);
            reject(new Error(reason));
          },
          resources: undefined // Typically not required for getAuthToken bootstrap token
        });
      });
    } catch (e: any) {
      console.error('[TeamsTab] Silent SSO error:', e);
      setError(e?.message || 'Silent SSO failed, user consent may be required');
      setStatus('error');
    }
  }, [exchangeToken]);

  const checkAuth = useCallback(async () => {
    console.log('[TeamsTab] Checking auth status with /auth/validate');
    try {
      const res = await fetch(`${getRouterBasename()}/auth/validate`, {
        method: 'GET',
        credentials: 'include'
      });
      console.log('[TeamsTab] Auth validate response status:', res.status);
      const json = await res.json().catch(() => ({}));
      console.log('[TeamsTab] Auth validate response:', json);
      setValidateInfo(json);
    } catch (e: any) {
      console.error('[TeamsTab] Auth validate error:', e);
      setValidateInfo({ error: e?.message || 'Failed to call /auth/validate' });
    }
  }, []);

  const openConsentPopup = useCallback(async () => {
    console.log('[TeamsTab] Opening consent popup');
    setError('');
    setStatus('authenticating');

    try {
      // Initialize for both v2 (app.initialize) and legacy v1 (initialize)
      const mt: any = microsoftTeams as any;
      if (mt?.app?.initialize) {
        console.log('[TeamsTab] Using Teams SDK v2 app.initialize() for popup');
        await mt.app.initialize();
      } else if (mt?.initialize) {
        console.log('[TeamsTab] Using Teams SDK v1 initialize() for popup');
        await mt.initialize();
      } else {
        console.log('[TeamsTab] Warning: No Teams SDK initialize method found for popup');
      }

      const popupUrl = `${window.location.origin}${getRouterBasename()}/teams-auth-start`;
      console.log('[TeamsTab] Opening popup with URL:', popupUrl);
      
      (microsoftTeams as any).authentication.authenticate({
        url: popupUrl,
        width: 600,
        height: 535,
        successCallback: async (_result: string) => {
          console.log('[TeamsTab] Popup authentication succeeded');
          // After popup success, retry silent token
          await getTokenSilently();
        },
        failureCallback: (message: string) => {
          console.log('[TeamsTab] Popup authentication failed:', message);
          setError(message || 'Popup authentication failed');
          setStatus('error');
        }
      });
    } catch (e: any) {
      console.error('[TeamsTab] Popup error:', e);
      setError(e?.message || 'Failed to open authentication popup');
      setStatus('error');
    }
  }, [getTokenSilently]);

  useEffect(() => {
    console.log('[TeamsTab] Component mounted, starting authentication flow');
    getTokenSilently();
  }, [getTokenSilently]);

  // Test cookie functionality and set Teams token
  useEffect(() => {
    // Test if regular cookies work
    document.cookie = "test_cookie=hello; path=/; SameSite=None; Secure";
    console.log("Test cookie set, current cookies:", document.cookie);
    
    // If we have a Teams token, store it in a cookie for subsequent requests
    if (teamsToken) {
      console.log('[TeamsTab] Storing Teams token in cookie for subsequent auth calls');
      document.cookie = `teams_auth_token=${teamsToken}; path=/; SameSite=None; Secure`;
    }
  }, [teamsToken]); // Re-run when teamsToken changes

  const renderBody = () => {
    if (status === 'authenticating' || status === 'init') {
      return (
        <div className="flex flex-col items-center justify-center gap-3">
          <div>Signing you in with Microsoft Teams SSO…</div>
          <div className="text-sm text-muted-foreground">Please wait</div>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="text-destructive text-center">Authentication error</div>
          {error ? <div className="text-sm text-muted-foreground text-center max-w-md">{error}</div> : null}
          <div className="flex gap-3">
            <button
              className="px-3 py-2 rounded bg-blue-600 text-white"
              onClick={getTokenSilently}
            >
              Retry silent SSO
            </button>
            <button
              className="px-3 py-2 rounded bg-gray-200"
              onClick={openConsentPopup}
            >
              Sign in with popup
            </button>
            <button
              className="px-3 py-2 rounded bg-gray-200"
              onClick={checkAuth}
            >
              Check /auth/validate
            </button>
          </div>
          {validateInfo ? (
            <pre className="text-xs bg-gray-100 p-3 rounded max-w-xl overflow-auto">
{JSON.stringify(validateInfo, null, 2)}
            </pre>
          ) : null}
        </div>
      );
    }

    // success
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div>Authenticated successfully.</div>
        <div className="flex gap-3">
          <button
            className="px-3 py-2 rounded bg-gray-200"
            onClick={checkAuth}
          >
            Check /auth/validate
          </button>
          <button
            className="px-3 py-2 rounded bg-blue-600 text-white"
            onClick={() => navigate('/', { replace: true })}
          >
            Continue to app
          </button>
        </div>
        {validateInfo ? (
          <pre className="text-xs bg-gray-100 p-3 rounded max-w-xl overflow-auto">
{JSON.stringify(validateInfo, null, 2)}
          </pre>
        ) : null}
      </div>
    );
  };

  return (
    <div className="w-full h-full min-h-[60vh] flex items-center justify-center p-6">
      {renderBody()}
    </div>
  );
}
